// src/components/admin/BrandingEditor.jsx
import React, { useState } from 'react';
import { ChromePicker } from 'react-color';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../utils/firebase';
import '../../styles/branding.css';

function BrandingEditor({ branding, onUpdate, salonId, onSave, saving, savedMessage, onPreview }) {
  const [name, setName] = useState(branding.name || 'iBeauty');
  const [colorPickerOpen, setColorPickerOpen] = useState(null);
  const [colors, setColors] = useState(branding.colors || {
    primary: '#E64C31',
    secondary: '#FFBDB1',
    accent: '#F6F6F6',
    background: '#FFFFFF',
    text: '#333333'
  });
  const [logoPosition, setLogoPosition] = useState(branding.logo?.position || 'left');
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({
    name: '',
    colors: ''
  });

  // Handle logo upload — uploads to Firebase Storage and stores the permanent URL
  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploading(true);

    try {
      // 1. Upload to Firebase Storage: salons/{salonId}/logos/{timestamp}_{filename}
      const timestamp = Date.now();
      const safeFileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, `salons/${salonId}/logos/${safeFileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // 2. Read image dimensions from the persistent URL
      const img = new Image();
      img.onload = () => {
        const newLogo = {
          url: downloadURL,
          width: img.width,
          height: img.height,
          position: logoPosition
        };

        onUpdate({
          ...branding,
          logo: newLogo
        });

        setUploading(false);
      };
      img.onerror = () => {
        // Dimensions unknown — still save with the download URL
        onUpdate({
          ...branding,
          logo: { url: downloadURL, width: null, height: null, position: logoPosition }
        });
        setUploading(false);
      };

      img.src = downloadURL;
    } catch (error) {
      console.error('Error uploading logo:', error);
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.svg']
    },
    maxFiles: 1
  });

  const handleColorChange = (color, colorType) => {
    const newColors = {
      ...colors,
      [colorType]: color.hex
    };

    setColors(newColors);
    validateColors();

    onUpdate({
      ...branding,
      colors: newColors
    });
  };

  const handlePickerClick = (e) => {
    // Prevent clicks inside the picker from closing it
    e.stopPropagation();
  };

  const handleColorInputChange = (e, colorType) => {
    let value = e.target.value.trim();
    
    // Add # if not present
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }
    
    // Validate hex color (3 or 6 digits)
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    
    if (hexRegex.test(value)) {
      const newColors = {
        ...colors,
        [colorType]: value.toUpperCase()
      };
      
      setColors(newColors);
      
      onUpdate({
        ...branding,
        colors: newColors
      });
    }
  };

  const validateName = (value) => {
    if (!value || value.trim() === '') {
      setErrors(prev => ({ ...prev, name: 'Business Name is mandatory. Please fill in this field.' }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, name: '' }));
      return true;
    }
  };

  const validateColors = () => {
    const hasAllColors = colors.primary && colors.secondary && colors.accent && colors.background && colors.text;
    if (!hasAllColors) {
      setErrors(prev => ({ ...prev, colors: 'All brand colors are mandatory. Please fill in all color fields.' }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, colors: '' }));
      return true;
    }
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setName(newName);
    validateName(newName);

    onUpdate({
      ...branding,
      name: newName
    });
  };

  const handleNameBlur = () => {
    validateName(name);
  };

  const handleLogoPositionChange = (e) => {
    const newPosition = e.target.value;
    setLogoPosition(newPosition);
    
    if (branding.logo) {
      onUpdate({
        ...branding,
        logo: {
          ...branding.logo,
          position: newPosition
        }
      });
    }
  };

  const handleRemoveLogo = () => {
    onUpdate({
      ...branding,
      logo: null
    });
  };

  return (
    <div className="branding-editor">
      <div className="editor-page-header">
        <h2>Brand Identity</h2>
        <div className="editor-page-actions">
          {savedMessage && <span className={`editor-save-msg${savedMessage.toLowerCase().includes('error') ? ' error' : ''}`}>{savedMessage}</span>}
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
          <button className="page-action-btn page-action-save" type="button" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
      
      <div className="form-group">
        <label>
          Business Name <span className="required-asterisk">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          placeholder="Enter your business name"
          className={errors.name ? 'input-error' : ''}
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>
      
      <div className="form-group">
        <label>Logo</label>
        <div {...getRootProps()} className="dropzone">
          <input {...getInputProps()} />
          {uploading ? (
            <p>Uploading logo to storage...</p>
          ) : branding.logo?.url ? (
            <div className="logo-preview">
              <img 
                src={branding.logo.url} 
                alt="Logo" 
                style={{ maxWidth: '200px', maxHeight: '100px' }} 
              />
              <p>Drag & drop a new logo or click to replace</p>
              <button 
                type="button" 
                className="remove-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveLogo();
                }}
              >
                Remove Logo
              </button>
            </div>
          ) : (
            <p>Drag & drop your logo here, or click to select a file</p>
          )}
        </div>
        
        <div className="form-group">
          <label>Logo Position</label>
          <select value={logoPosition} onChange={handleLogoPositionChange}>
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      </div>
      
      <div className="form-group">
        <label>
          Brand Colors <span className="required-asterisk">*</span>
        </label>
        <div className="color-instruction">
          💡 Click the color box to open the color picker, or type a hex color code directly (e.g., #E64C31).
          These colors will be applied across all customer-facing pages including Dashboard, Loyalty Program, Profile, and Booking pages.
        </div>
        {errors.colors && <span className="error-message">{errors.colors}</span>}
        <div className="color-pickers">
          {Object.entries(colors).map(([colorType, colorValue]) => (
            <div key={colorType} className="color-picker-item">
              <label>{colorType.charAt(0).toUpperCase() + colorType.slice(1)}</label>
              <div className="color-input-group">
                <div 
                  className="color-preview" 
                  style={{ backgroundColor: colorValue }}
                  onClick={() => setColorPickerOpen(colorType)}
                  title="Click to open color picker"
                />
                <input
                  type="text"
                  className="color-hex-input"
                  value={colorValue}
                  onChange={(e) => setColors({ ...colors, [colorType]: e.target.value })}
                  onBlur={(e) => handleColorInputChange(e, colorType)}
                  placeholder="#FFFFFF"
                  maxLength={7}
                />
              </div>
              {colorPickerOpen === colorType && (
                <div className="color-picker-popover" onClick={handlePickerClick}>
                  <div 
                    className="color-picker-cover" 
                    onClick={() => setColorPickerOpen(null)}
                  />
                  <div className="color-picker-wrapper">
                    <ChromePicker 
                      color={colorValue} 
                      onChange={(color) => handleColorChange(color, colorType)} 
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BrandingEditor;